import type {
  GetApiV1ThreeLetterCodePartnerAdminsClientsClientId200,
  GetApiV1ThreeLetterCodePartnerAdminsCustomFieldDefinitions200,
  GetApiV1ThreeLetterCodePartnerAdminsGroups200,
  GetApiV1ThreeLetterCodePartnerAdminsPartnerBranches200,
  GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200,
  GetApiV1ThreeLetterCodePublicPrefectures200,
} from '@fukuri-admins/apis/client/gen/model';
import { type UseFormReturn, useForm, useNavigate } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useGetPartnerAdminsPartnerBranches,
  useGetPartnerAdminsPartnerFeatureFlags,
} from '~/hooks';
import { getClientLink } from '~/utils/getLinks';
import {
  convertToCustomFieldDefinitions,
  convertToFormValues,
  generateRequestBody,
} from '../logics';
import { type EditClientFormFieldValues, editClientFormSchema } from '../logics/schema';
import { EditClientFormPresentation } from '../presentations';
import { useGetClient } from './useGetClient';
import { useGetCustomFieldDefinitions } from './useGetCustomFieldDefinitions';
import { useGetGroups } from './useGetGroups';
import { useGetPublicPrefectures } from './useGetPublicPrefectures';
import { usePutPartnerAdminsClient } from './usePutPartnerAdminsClient';

type Args = {
  /** クライアントID（数値）*/
  clientId: number;
};

type PresentationProps = React.ComponentProps<typeof EditClientFormPresentation>;

type IUseEditClientForm = (args: Args) => {
  state: {
    methods: UseFormReturn<EditClientFormFieldValues>;
    isButtonDisabled: boolean;
    clientName: string;
    prefectures: GetApiV1ThreeLetterCodePublicPrefectures200;
    partnerBranches: GetApiV1ThreeLetterCodePartnerAdminsPartnerBranches200;
    groups: GetApiV1ThreeLetterCodePartnerAdminsGroups200;
    partnerFeatureFlags: GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200;
    clientData: GetApiV1ThreeLetterCodePartnerAdminsClientsClientId200;
    customFieldDefinitions: GetApiV1ThreeLetterCodePartnerAdminsCustomFieldDefinitions200;
  };
  actions: {
    handleOnSubmit: PresentationProps['onSubmit'];
    handleCancel: PresentationProps['onCancel'];
  };
};

/**
 * 利用企業編集フォームのカスタムフック（新版）
 *
 * @returns state.methods - react-hook-form のメソッド
 * @returns state.isButtonDisabled - ボタン無効化フラグ
 * @returns state.clientName - 企業名（タイトル表示用）
 * @returns state.prefectures - 都道府県一覧（public エンドポイント）
 * @returns state.partnerBranches - 取引店舗一覧（PartnerAdmin 専用エンドポイント。PEN-994）
 * @returns state.groups - グループ一覧
 * @returns state.partnerFeatureFlags - パートナー機能フラグ一覧
 * @returns state.clientData - クライアントAPIレスポンス
 * @returns state.customFieldDefinitions - カスタムフィールド定義
 * @returns actions.handleOnSubmit - フォーム送信ハンドラ
 * @returns actions.handleCancel - キャンセルハンドラ
 */
export const useEditClientForm: IUseEditClientForm = ({ clientId }) => {
  const navigate = useNavigate();
  const threeLetterCode = getThreeLetterCode();
  const { showToast } = useToast();

  const { data: clientData } = useGetClient({ threeLetterCode, clientId });
  const { data: prefecturesData } = useGetPublicPrefectures({ threeLetterCode });
  const { data: partnerBranchesData } = useGetPartnerAdminsPartnerBranches({ threeLetterCode });
  const { data: groupsData } = useGetGroups({ threeLetterCode });
  const { data: partnerFeatureFlagsData } = useGetPartnerAdminsPartnerFeatureFlags({
    threeLetterCode,
  });
  const { data: customFieldDefinitionsData } = useGetCustomFieldDefinitions({ threeLetterCode });

  const clientName = clientData.client.name;

  const methods = useForm<EditClientFormFieldValues>({
    defaultValues: convertToFormValues({ clientData }),
    criteriaMode: 'all',
    mode: 'onTouched',
    // 取引店舗の選択肢が1件以上あるときのみ partnerBranchId を必須にする（PEN-900）
    resolver: zodResolver(
      editClientFormSchema({
        isPartnerBranchRequired: partnerBranchesData.partner_branches.length > 0,
      })
    ),
  });

  const { trigger, isMutating } = usePutPartnerAdminsClient(threeLetterCode, clientId, {
    onSuccess: ({ client: { id, name } }) => {
      showToast({
        message: `「${name}」を更新しました。`,
        status: 'success',
      });
      navigate(getClientLink(id));
    },
    onError: error => {
      if (error.response?.status === 422) {
        const errors = error.response?.data?.errors;
        showToast({
          message: errors?.[0] ?? '利用企業の更新に失敗しました。',
          status: 'danger',
        });
        return;
      }

      showToast({
        message: '利用企業の更新に失敗しました。',
        status: 'danger',
      });
    },
  });

  /**
   * フォームを送信する
   */
  const handleOnSubmit: PresentationProps['onSubmit'] = methods.handleSubmit(async formValues => {
    const body = generateRequestBody({
      formValues,
      allFeatureFlags: clientData.client.client_feature_flags ?? [],
      customFieldDefinitions: convertToCustomFieldDefinitions(customFieldDefinitionsData),
    });
    await trigger(body);
  });

  /**
   * キャンセルボタンをクリックして利用企業詳細に戻る
   */
  const handleCancel: PresentationProps['onCancel'] = () => {
    navigate(getClientLink(clientId));
  };

  const isButtonDisabled =
    methods.formState.isSubmitting || !methods.formState.isValid || isMutating;

  return {
    state: {
      methods,
      isButtonDisabled,
      clientName,
      prefectures: prefecturesData,
      partnerBranches: partnerBranchesData,
      groups: groupsData,
      partnerFeatureFlags: partnerFeatureFlagsData,
      clientData,
      customFieldDefinitions: customFieldDefinitionsData,
    },
    actions: {
      handleOnSubmit,
      handleCancel,
    },
  };
};
